import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import { useAuth } from '../context/AuthContext';
import './BillingManager.css';

const BillingManager = () => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (user) {
      fetchSubscriptionStatus();
      fetchInvoices();
    }
  }, [user]);

  const fetchSubscriptionStatus = async () => {
    try {
      console.log('[BillingManager] Fetching subscription status...');
      const response = await axios.get(`${API_URL}/api/subscriptions/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[BillingManager] Subscription response:', response.data);
      
      if (response.data && response.data.success && response.data.subscription) {
        setSubscription(response.data.subscription);
        console.log('[BillingManager] ✅ Subscription data set:', response.data.subscription);
      } else {
        console.warn('[BillingManager] ⚠️ Invalid subscription response:', response.data);
        setSubscription(null);
      }
    } catch (error) {
      console.error('[BillingManager] ❌ Error fetching subscription status:', error);
      console.error('[BillingManager] Error response:', error.response?.data);
      console.error('[BillingManager] Error status:', error.response?.status);
      
      // If subscription endpoint fails, try to get from tenant info
      if (error.response?.status === 404 || error.response?.status === 403) {
        console.log('[BillingManager] Trying to get subscription from tenant info...');
        try {
          // Get tenant subscription info from user context or tenant API
          if (user?.tenantId) {
            // Subscription data might be in user context or we need to fetch tenant
            // For now, set to null and show "No active subscription"
            setSubscription(null);
          }
        } catch (tenantError) {
          console.error('[BillingManager] Error getting tenant info:', tenantError);
        }
      }
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      console.log('[BillingManager] Fetching invoices...');
      const response = await axios.get(`${API_URL}/api/billing/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[BillingManager] Invoices response:', response.data);
      
      if (response.data && response.data.success) {
        setInvoices(response.data.data || []);
        console.log('[BillingManager] ✅ Invoices data set:', response.data.data?.length || 0, 'invoices');
      } else {
        console.warn('[BillingManager] ⚠️ Invalid invoices response:', response.data);
        setInvoices([]);
      }
    } catch (error) {
      console.error('[BillingManager] ❌ Error fetching invoices:', error);
      console.error('[BillingManager] Error response:', error.response?.data);
      console.error('[BillingManager] Error status:', error.response?.status);
      setInvoices([]);
    }
  };

  const handleCreatePayment = async (invoiceId) => {
    setPaymentLoading(true);
    try {
      const invoice = invoices.find(inv => inv._id === invoiceId);
      if (!invoice) {
        toast.error('Invoice not found');
        return;
      }

      const response = await axios.post(`${API_URL}/api/billing/create-payment`, {
        invoiceId,
        plan: invoice.plan,
        billingCycle: invoice.billingCycle
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Load Razorpay script
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => {
        const options = {
          key: response.data.keyId,
          amount: response.data.amount,
          currency: response.data.currency,
          name: 'Subscription Payment',
          description: `Payment for ${invoice.plan} plan`,
          order_id: response.data.orderId,
          handler: async function (razorpayResponse) {
            try {
              await axios.post(`${API_URL}/api/billing/verify-payment`, {
                razorpayOrderId: razorpayResponse.razorpay_order_id,
                razorpayPaymentId: razorpayResponse.razorpay_payment_id,
                razorpaySignature: razorpayResponse.razorpay_signature,
                invoiceId
              }, {
                headers: { Authorization: `Bearer ${token}` }
              });
              toast.success('Payment successful! Subscription activated.');
              fetchSubscriptionStatus();
              fetchInvoices();
            } catch (error) {
              toast.error('Payment verification failed');
              console.error(error);
            }
          },
          prefill: {
            email: '',
            contact: ''
          },
          theme: {
            color: '#3399cc'
          }
        };

        const razorpay = new window.Razorpay(options);
        razorpay.open();
      };
      document.body.appendChild(script);
    } catch (error) {
      toast.error('Error creating payment order');
      console.error(error);
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleUpgradePlan = async (newPlan, billingCycle) => {
    setPaymentLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/billing/upgrade-plan`, {
        newPlan,
        billingCycle
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.proratedAmount === 0) {
        toast.success('Plan upgraded successfully!');
        fetchSubscriptionStatus();
      } else {
        // Create payment for upgrade
        const invoiceId = response.data.invoice._id;
        await handleCreatePayment(invoiceId);
      }
    } catch (error) {
      toast.error('Error upgrading plan');
      console.error(error);
    } finally {
      setPaymentLoading(false);
      setShowUpgradeModal(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const plans = {
    free: { monthly: 0, yearly: 0, name: 'Free' },
    basic: { monthly: 999, yearly: 9999, name: 'Basic' },
    pro: { monthly: 2999, yearly: 29999, name: 'Pro' },
    enterprise: { monthly: 9999, yearly: 99999, name: 'Enterprise' }
  };

  if (loading) {
    return <div className="billing-manager loading">Loading billing information...</div>;
  }

  return (
    <div className="billing-manager">
      <div className="page-header">
        <h1>Billing & Subscription</h1>
        <p>Manage your subscription and view invoices</p>
      </div>

      <div className="billing-content">
        {/* Current Subscription */}
        <div className="subscription-card">
          <h2>Current Subscription</h2>
          {subscription ? (
            <div className="subscription-details">
              <div className="detail-row">
                <span className="label">Plan:</span>
                <span className="value">{subscription.plan}</span>
              </div>
              <div className="detail-row">
                <span className="label">Billing Cycle:</span>
                <span className="value">{subscription.billingCycle}</span>
              </div>
              <div className="detail-row">
                <span className="label">Status:</span>
                <span className={`value status-${subscription.status}`}>{subscription.status}</span>
              </div>
              <div className="detail-row">
                <span className="label">Start Date:</span>
                <span className="value">{formatDate(subscription.startDate)}</span>
              </div>
              <div className="detail-row">
                <span className="label">End Date:</span>
                <span className="value">{formatDate(subscription.endDate)}</span>
              </div>
              {subscription.daysRemaining !== null && (
                <div className="detail-row">
                  <span className="label">Days Remaining:</span>
                  <span className="value">{subscription.daysRemaining} days</span>
                </div>
              )}
              <div className="detail-row">
                <span className="label">Auto Renew:</span>
                <span className="value">{subscription.autoRenew ? 'Yes' : 'No'}</span>
              </div>
              <button
                className="btn btn-primary"
                onClick={() => setShowUpgradeModal(true)}
                disabled={paymentLoading}
              >
                Upgrade Plan
              </button>
            </div>
          ) : (
            <p>No active subscription</p>
          )}
        </div>

        {/* Invoices */}
        <div className="invoices-section">
          <h2>Invoices</h2>
          <table className="table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Plan</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Period</th>
                <th>Due Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center">No invoices found</td>
                </tr>
              ) : (
                invoices.map((invoice) => (
                  <tr key={invoice._id}>
                    <td>{invoice.invoiceNumber}</td>
                    <td>{invoice.plan}</td>
                    <td>{formatCurrency(invoice.total)}</td>
                    <td>
                      <span className={`badge badge-${invoice.status}`}>{invoice.status}</span>
                    </td>
                    <td>
                      {formatDate(invoice.periodStart)} - {formatDate(invoice.periodEnd)}
                    </td>
                    <td>{formatDate(invoice.dueDate)}</td>
                    <td>
                      {invoice.status === 'pending' && invoice.total > 0 && (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleCreatePayment(invoice._id)}
                          disabled={paymentLoading}
                        >
                          Pay Now
                        </button>
                      )}
                      {invoice.status === 'paid' && (
                        <a
                          href={`${API_URL}/api/billing/invoices/${invoice._id}`}
                          className="btn btn-sm btn-secondary"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Download
                        </a>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Upgrade Modal */}
        {showUpgradeModal && (
          <div className="modal-overlay" onClick={() => setShowUpgradeModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Upgrade Plan</h2>
              <div className="plans-grid">
                {Object.entries(plans).map(([planKey, plan]) => {
                  if (planKey === subscription?.plan) return null;
                  return (
                    <div key={planKey} className="plan-option">
                      <h3>{plan.name}</h3>
                      <div className="plan-pricing">
                        <div>
                          <span className="price">{formatCurrency(plan.monthly)}</span>
                          <span className="period">/month</span>
                        </div>
                        <div>
                          <span className="price">{formatCurrency(plan.yearly)}</span>
                          <span className="period">/year</span>
                        </div>
                      </div>
                      <div className="plan-actions">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleUpgradePlan(planKey, 'monthly')}
                          disabled={paymentLoading}
                        >
                          Monthly
                        </button>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleUpgradePlan(planKey, 'yearly')}
                          disabled={paymentLoading}
                        >
                          Yearly
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              <button
                className="btn btn-secondary"
                onClick={() => setShowUpgradeModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingManager;

